import React, { useState } from "react";
import { FaCamera } from "react-icons/fa";
import styles from "./ColorVariantsEditor.module.css";

const cloudinaryCloudName = "dk1nepgzk";
const uploadPreset = "drxz9h8c";

async function uploadToCloudinary(file) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", uploadPreset);
  const response = await fetch(
    `https://api.cloudinary.com/v1_1/${cloudinaryCloudName}/image/upload`,
    { method: "POST", body: formData }
  );
  const data = await response.json();
  if (!response.ok) throw new Error(data.error?.message || "Image upload failed");
  return data.secure_url;
}

// Common garment colours — picking a swatch auto-fills the nearest name
// (only while the name field is empty or still holds a previous suggestion).
const NAMED_COLORS = [
  ["Black", "#141210"], ["Charcoal", "#3a3a3a"], ["Grey", "#8a8a8a"],
  ["Silver", "#c9c9c9"], ["White", "#ffffff"], ["Off-White", "#f4f1ea"],
  ["Cream", "#f1e5c8"], ["Beige", "#d9c7a7"], ["Tan", "#c3986b"],
  ["Camel", "#b3824a"], ["Brown", "#6f4a2f"], ["Chocolate", "#4a2c1a"],
  ["Khaki", "#a99b6c"], ["Olive", "#6b6b3a"], ["Camo Green", "#4a5d3a"],
  ["Forest Green", "#1f4d2e"], ["Green", "#2e8b3d"], ["Mint", "#a8e0c2"],
  ["Teal", "#1f6b6b"], ["Turquoise", "#30c5c9"], ["Sky Blue", "#8ec5e8"],
  ["Blue", "#2a5bd7"], ["Royal Blue", "#1a3fa8"], ["Navy", "#1a2340"],
  ["Denim", "#3b5a80"], ["Indigo", "#3a2f8f"], ["Purple", "#6d3a9e"],
  ["Lavender", "#b9a8e0"], ["Pink", "#e88ab8"], ["Hot Pink", "#e0338a"],
  ["Blush", "#efc3c3"], ["Red", "#c92a2a"], ["Wine", "#7a1f33"],
  ["Burgundy", "#5c1a26"], ["Maroon", "#4a1518"], ["Orange", "#e07020"],
  ["Burnt Orange", "#b35418"], ["Peach", "#f2b98e"], ["Mustard", "#cfa227"],
  ["Yellow", "#e8cf3a"], ["Gold", "#b8912f"],
];

const hexToRgb = (hex) => {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
};

const nearestColorName = (hex) => {
  const [r, g, b] = hexToRgb(hex);
  let best = NAMED_COLORS[0][0];
  let bestDist = Infinity;
  for (const [name, ref] of NAMED_COLORS) {
    const [nr, ng, nb] = hexToRgb(ref);
    // weighted for perception: eyes are most sensitive to green
    const dist = 2 * (r - nr) ** 2 + 4 * (g - ng) ** 2 + 3 * (b - nb) ** 2;
    if (dist < bestDist) {
      bestDist = dist;
      best = name;
    }
  }
  return best;
};

// Editor for a product's colour variants: [{name, hex, image, images}].
// Photos upload to Cloudinary as soon as they're picked, so the parent form
// just submits URLs. The first photo of each colour is its cover.
const ColorVariantsEditor = ({ colors, onChange, onError, onBusyChange }) => {
  const [uploadingIndex, setUploadingIndex] = useState(null);

  const setBusy = (index) => {
    setUploadingIndex(index);
    if (onBusyChange) onBusyChange(index !== null);
  };

  const update = (index, patch) => {
    onChange(colors.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  };

  // Picking a swatch names the colour automatically — unless the admin
  // already typed their own name, which we never overwrite.
  const handleSwatchChange = (index, hex) => {
    const color = colors[index];
    const previousSuggestion = nearestColorName(color.hex || "#141210");
    const shouldAutoName =
      !color.name.trim() || color.name === previousSuggestion;
    update(index, {
      hex,
      ...(shouldAutoName ? { name: nearestColorName(hex) } : {}),
    });
  };

  const addColor = () => {
    onChange([...colors, { name: "", hex: "#141210", image: "", images: [] }]);
  };

  const removeColor = (index) => {
    onChange(colors.filter((_, i) => i !== index));
  };

  const allImages = (color) =>
    [color.image, ...(color.images || [])].filter(Boolean);

  const handleFiles = async (index, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(index);
    try {
      const urls = [];
      for (const file of files) {
        urls.push(await uploadToCloudinary(file));
      }
      const color = colors[index];
      const merged = [...allImages(color), ...urls];
      update(index, { image: merged[0] || "", images: merged.slice(1) });
    } catch (err) {
      if (onError) onError(err.message);
    } finally {
      setBusy(null);
    }
  };

  const removeImage = (index, imageUrl) => {
    const merged = allImages(colors[index]).filter((u) => u !== imageUrl);
    update(index, { image: merged[0] || "", images: merged.slice(1) });
  };

  return (
    <div className={styles.wrap}>
      {colors.map((color, index) => {
        const photos = allImages(color);
        const label = color.name.trim() || "this colour";
        return (
          <div className={styles.card} key={index}>
            <div className={styles.head}>
              <label className={styles.swatchWrap} title="Pick the swatch colour shoppers will tap">
                <input
                  type="color"
                  className={styles.swatchInput}
                  value={color.hex || "#141210"}
                  onChange={(e) => handleSwatchChange(index, e.target.value)}
                />
                <span className={styles.swatchLabel}>Swatch</span>
              </label>
              <input
                className={styles.nameInput}
                placeholder="Colour name (e.g. Black)"
                required
                value={color.name}
                onChange={(e) => update(index, { name: e.target.value })}
              />
              <button
                type="button"
                className={styles.removeColor}
                onClick={() => removeColor(index)}
              >
                Remove colour
              </button>
            </div>

            <p className={styles.photosLabel}>
              Product photos in {label}
              {photos.length > 0 && ` · ${photos.length}`}
            </p>

            <div className={styles.photoRow}>
              {photos.map((url, photoIndex) => (
                <div className={styles.thumb} key={url}>
                  <img src={url} alt={`${label} ${photoIndex + 1}`} />
                  {photoIndex === 0 && <span className={styles.coverBadge}>Cover</span>}
                  <button
                    type="button"
                    className={styles.thumbRemove}
                    onClick={() => removeImage(index, url)}
                    aria-label="Remove photo"
                  >
                    ×
                  </button>
                </div>
              ))}

              <label className={styles.uploadBtn}>
                <FaCamera aria-hidden="true" />
                {uploadingIndex === index
                  ? "Uploading…"
                  : photos.length
                  ? "Add more photos"
                  : "Upload photos"}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    handleFiles(index, e.target.files);
                    e.target.value = "";
                  }}
                />
              </label>
            </div>

            {photos.length === 0 && (
              <p className={styles.emptyHint}>
                Optional: upload photos of the garment in {label} — shoppers see
                them when they tap this swatch (the first becomes its cover).
                Without photos, the swatch still works and shows the product's
                main photos.
              </p>
            )}
          </div>
        );
      })}

      <button type="button" className={styles.addColor} onClick={addColor}>
        ＋ Add another colour
      </button>
    </div>
  );
};

export default ColorVariantsEditor;
